import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { SectionLabel } from '../layout/Typography';
import { cn } from '../../lib/utils';

interface SidebarSectionProps {
  label: ReactNode;
  /** Optional icon rendered alongside the label. */
  icon?: ReactNode;
  /** Where the icon sits relative to the label. Defaults to `'before'`. */
  iconPosition?: 'before' | 'after';
  /** Optional action buttons rendered on the right side of the header row. */
  actions?: ReactNode;
  /** Controlled open state. When omitted, falls back to `defaultOpen`. */
  open?: boolean;
  /** Called when the section is toggled. */
  onOpenChange?: (open: boolean) => void;
  /** Initial open state for uncontrolled usage. Defaults to `true`. */
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
  'data-testid'?: string;
  triggerTestId?: string;
}

export function SidebarSection({
  label,
  icon,
  iconPosition = 'before',
  actions,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = true,
  className,
  children,
  'data-testid': testId,
  triggerTestId,
}: SidebarSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  function toggle() {
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  }

  return (
    <div data-testid={testId} className={cn('group/section', className)}>
      <div className="flex items-center pr-1">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={isOpen}
          data-testid={triggerTestId}
          className="group/trigger flex flex-1 items-center gap-2 rounded-md px-3 py-1.5 transition-colors hover:bg-sidebar-accent/50"
        >
          <ChevronDown
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground/50 transition-transform duration-200 group-hover/trigger:text-muted-foreground',
              !isOpen && '-rotate-90',
            )}
          />
          {iconPosition === 'before' && icon}
          <SectionLabel as="span" className="text-2xs">
            {label}
          </SectionLabel>
          {iconPosition === 'after' && icon}
        </button>
        {actions}
      </div>
      {isOpen && children}
    </div>
  );
}
